/**
 * Zod validation schemas for Discount types
 */

import { z } from 'zod';

// Define discount types locally to avoid package resolution issues
export enum DiscountType {
  FLAT = 'flat',
  PERCENTAGE = 'percentage',
  BUY_X_GET_Y = 'buy_x_get_y',
  SEASONAL = 'seasonal',
  BULK = 'bulk',
}

export const discountTypeEnum = z.nativeEnum(DiscountType);

export const discountSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().optional(),
  discount_type: discountTypeEnum,
  value: z.number().nonnegative(),
  min_order_value: z.number().nonnegative().optional(),
  max_order_value: z.number().nonnegative().optional(),
  max_discount_amount: z.number().nonnegative().optional(),
  valid_from: z.string(),
  valid_until: z.string(),
  usage_limit: z.number().positive().optional(),
  current_usage: z.number().nonnegative(),
  is_active: z.boolean(),
  is_stackable: z.boolean(),
  priority: z.number().int().nonnegative(),
  applicable_products: z.string().optional(),
  applicable_categories: z.string().optional(),
  applicable_warehouses: z.string().optional(),
  excluded_products: z.string().optional(),
  excluded_categories: z.string().optional(),
  buy_quantity: z.number().positive().optional(),
  get_quantity: z.number().positive().optional(),
  get_discount_type: z.string().optional(),
  get_discount_value: z.number().nonnegative().optional(),
  terms: z.string().optional(),
  status: z.enum(['active', 'expired', 'inactive', 'usage_limit_reached', 'scheduled']),
  created_at: z.string(),
  updated_at: z.string(),
});

export const createDiscountSchema = z.object({
  code: z.string().min(1, 'Discount code is required'),
  name: z.string().min(1, 'Discount name is required'),
  description: z.string().optional(),
  discount_type: discountTypeEnum,
  value: z.number().nonnegative().optional(),
  min_order_value: z.number().nonnegative().optional(),
  max_order_value: z.number().nonnegative().optional(),
  max_discount_amount: z.number().nonnegative().optional(),
  valid_from: z.string().min(1, 'Valid from date is required'),
  valid_until: z.string().min(1, 'Valid until date is required'),
  usage_limit: z.number().positive().optional(),
  is_active: z.boolean().optional(),
  is_stackable: z.boolean().optional(),
  priority: z.number().int().nonnegative().optional(),
  applicable_products: z.string().optional(),
  applicable_categories: z.string().optional(),
  applicable_warehouses: z.string().optional(),
  excluded_products: z.string().optional(),
  excluded_categories: z.string().optional(),
  buy_quantity: z.number().positive().optional(),
  get_quantity: z.number().positive().optional(),
  get_discount_type: z.string().optional(),
  get_discount_value: z.number().nonnegative().optional(),
  terms: z.string().optional(),
});

export const updateDiscountSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  value: z.number().nonnegative().optional(),
  min_order_value: z.number().nonnegative().optional(),
  max_order_value: z.number().nonnegative().optional(),
  max_discount_amount: z.number().nonnegative().optional(),
  valid_from: z.string().optional(),
  valid_until: z.string().optional(),
  usage_limit: z.number().positive().optional(),
  is_active: z.boolean().optional(),
  is_stackable: z.boolean().optional(),
  priority: z.number().int().nonnegative().optional(),
  applicable_products: z.string().optional(),
  applicable_categories: z.string().optional(),
  applicable_warehouses: z.string().optional(),
  excluded_products: z.string().optional(),
  excluded_categories: z.string().optional(),
  buy_quantity: z.number().positive().optional(),
  get_quantity: z.number().positive().optional(),
  get_discount_type: z.string().optional(),
  get_discount_value: z.number().nonnegative().optional(),
  terms: z.string().optional(),
});

export const discountValidationSchema = z.object({
  is_valid: z.boolean(),
  message: z.string(),
  discount_id: z.string().optional(),
  discount_code: z.string().optional(),
  discount_name: z.string().optional(),
  discount_type: z.string().optional(),
  value: z.number().optional(),
  calculated_discount: z.number().optional(),
  max_discount_amount: z.number().optional(),
});

export const discountUsageSchema = z.object({
  id: z.string(),
  discount_id: z.string(),
  sale_id: z.string(),
  amount: z.number().nonnegative(),
  used_at: z.string(),
  created_at: z.string(),
});

export type Discount = z.infer<typeof discountSchema>;
export type CreateDiscount = z.infer<typeof createDiscountSchema>;
export type UpdateDiscount = z.infer<typeof updateDiscountSchema>;
export type DiscountValidation = z.infer<typeof discountValidationSchema>;
export type DiscountUsage = z.infer<typeof discountUsageSchema>;
